import React, { Component } from 'react';
import cn from 'classnames';
import { connect } from 'react-redux';
import { parse } from 'query-string';
import { injectIntl, intlShape, FormattedMessage } from 'react-intl';
import { PopoverBody, Popover, UncontrolledTooltip } from 'reactstrap';
import { bool, array, func } from 'prop-types';
import { decimalAdjust, formatToCurrency } from '../../../utils/utils';
import { setRedirectToPaymentsData } from '../../../actions/finances';
import { PaymentsOrdersFormView } from '../index';
import Icon from '../../Icon/Icon';

/**
 * Get default payment config
 * @return {Object} Default config
 */
const getDefaultRules = () => ({
  minPayment: null, //минимальный платеж
  maxPaymentDay: null, //дневной лимит
  maxPaymentMonth: null, //месячный лимит
  commission: 0, //размер комиссии
  showAlerts: null,
  messageAlert: null,
  fixedCommission: 0, //размер фиксированной комиссии
  commissionText: null, //текст размера комиссии
  wallets: [],
  maxPayment: 0, //лимит на выплату за раз
  chunksAllowed: false, //при превышении maxPayment можно дробить на несколько выплат

  sumRules: {
    //Правила для "Сумма выплаты"
    _isFloat: true, //разрешить копейки
    _round: 0, //округлять вниз/вверх/не надо (-1, 1, 0)
    _toFix: 0, //число, которому должно быть кратно
  },

  totalRules: {
    //Правила для "К выплате"
    _isFloat: true, //разрешить копейки
    _round: 0, //округлять вниз/вверх/не надо (-1, 1, 0)
    _toFix: 0, //число, которому должно быть кратно
    _minus: 0, //фиксированная комиссия, которую вычитать дополнительно
  },
});

const applyTotalRules = (amount, totalRules, reverse) => {
  // reverse - если направление "К выплате" -> "Сумма выплаты"
  let result = amount;
  /*if (reverse && totalRules._minus) {
    result = Math.max(0, result + totalRules._minus);
  }*/

  if (totalRules._isFloat) {
    result =
      totalRules._round === 0
        ? result
        : totalRules._round > 0
        ? reverse
          ? decimalAdjust('floor', result, -1)
          : decimalAdjust('ceil', result, -1)
        : reverse
        ? decimalAdjust('ceil', result, -1)
        : decimalAdjust('floor', result, -1);
  } else {
    result =
      totalRules._round == 0
        ? result
        : totalRules._round > 0
        ? Math.round(result)
        : reverse
        ? Math.ceil(result)
        : Math.floor(result);

    if (totalRules._toFix > 0) {
      result = result - (result % totalRules._toFix);
    }
  }

  /*if (!reverse && totalRules._minus) {
    result = Math.max(0, result - totalRules._minus);
  }*/

  return result;
};

const getTotal = (amount = 0, rules = {}, paymentCurrency, toCurrency, rates, purseId) => {
  const commission = parseFloat(getCommission(purseId, rules));

  let sumToPayout = parseFloat(amount);
  sumToPayout = convertAmount(sumToPayout, paymentCurrency, toCurrency, rates);

  if (isNaN(sumToPayout)) return '';

  const { totalRules, maxPayment, chunksAllowed } = rules;

  if (commission !== 0 || rules.fixedCommission > 0) {
    // const maxPayment = 15000;
    // const chunksAllowed = true;

    if (maxPayment && chunksAllowed) {
      let maxSum = maxPayment;

      if (totalRules._minus) {
        maxSum -= Number(totalRules._minus);
      }
      maxSum = Math.ceil(maxSum / ((100 + commission) / 100));

      // если сумма к выплате больше максимальной, бьем на куски и считаем для кажлого отдельно
      if (sumToPayout / maxSum > 1) {
        // sumToPayout уже включает все комиссии
        const numberOfFullSumChunks = Math.floor(sumToPayout / maxSum);
        let remainder = sumToPayout - maxSum * numberOfFullSumChunks;
        // считаем только для остатка
        remainder = remainder + (remainder * commission) / 100;
        if (totalRules) {
          remainder = applyTotalRules(remainder, totalRules);
        }

        return remainder + maxPayment * numberOfFullSumChunks;
      }
    }

    let total = sumToPayout * 1 + (sumToPayout * commission) / 100;

    if (totalRules) {
      total = applyTotalRules(total, totalRules);
    }
    if (rules.fixedCommission > 0) {
      const purse = rules.wallets.find((w) => w.id === purseId);
      total -= convertAmount(
        rules.fixedCommission,
        toCurrency,
        (purse && purse.currency) || paymentCurrency,
        rates,
      );
      total = Math.max(total, 0);
    }

    return formatToCurrency(total).replace(/,/g, '');
  }

  return formatToCurrency(sumToPayout).replace(/,/g, '');
};

const getInitialFromTotal = (
  amount = 0,
  rules = {},
  paymentCurrency,
  toCurrency,
  rates,
  purseId,
) => {
  let initialSum = parseFloat(amount);
  initialSum = convertAmount(initialSum, toCurrency, paymentCurrency, rates);

  if (isNaN(initialSum)) return '';

  const commission = parseFloat(getCommission(purseId, rules));
  const { totalRules, maxPayment, chunksAllowed } = rules;

  if (commission !== 0 || rules.fixedCommission > 0) {
    // const maxPayment = 15000;
    // const chunksAllowed = true;
    if (maxPayment && chunksAllowed && initialSum / maxPayment > 1) {
      const numberOfFullMaxPaymentChunks = Math.floor(initialSum / maxPayment);

      // Full chunks
      let initialSumForOneChunk = maxPayment;
      if (totalRules) {
        initialSumForOneChunk = applyTotalRules(initialSumForOneChunk, totalRules, true);
      }
      initialSumForOneChunk = Math.ceil(initialSumForOneChunk / ((100 + commission) / 100));

      const initialForFullParts = initialSumForOneChunk * numberOfFullMaxPaymentChunks;

      // Remainder
      const remainder = initialSum - maxPayment * numberOfFullMaxPaymentChunks;
      let initialForRemainder = remainder;
      if (totalRules) {
        initialForRemainder = applyTotalRules(initialForRemainder, totalRules, true);
      }
      initialForRemainder = Math.ceil(initialForRemainder / ((100 + commission) / 100));

      return initialForFullParts + initialForRemainder;
    }

    if (totalRules) {
      initialSum = applyTotalRules(initialSum, totalRules, true);
    }
    initialSum = Math.ceil(initialSum / ((100 + commission) / 100));

    if (rules.fixedCommission > 0) {
      initialSum += convertAmount(rules.fixedCommission, toCurrency, paymentCurrency, rates);
    }

    return formatToCurrency(initialSum).replace(/,/g, '');
  }

  return isNaN(initialSum) ? 0 : formatToCurrency(initialSum).replace(/,/g, '');
};

const getCommission = (purseId, rules) => {
  const purse = rules.wallets.find((w) => w.id === purseId);
  return purse && purse.bonus ? purse.bonus : rules.commission;
};

const convertAmount = (amount, from, to, rates) => {
  if (!rates) return amount;

  let adjustedFrom = from === 'RUB' ? from + from : to + from;
  let adjustedTo = to === 'RUB' ? to + to : from + to;

  const rate = from === to ? 1 : rates[adjustedFrom] / rates[adjustedTo];

  const result = amount * rate;
  // result is float

  if (0 && result && result % 1 !== 0) {
    // temporarily disabled (usdt minimal payment calculation bug)
    const resultAsSplitString = result.toString().split('.');
    const wholePart = resultAsSplitString[0];
    // cut fraction part to 2 digits
    const fractionPart =
      resultAsSplitString[1].length > 2
        ? resultAsSplitString[1].slice(0, 2)
        : resultAsSplitString[1];

    if (to === 'RUB') {
      return Math.ceil(result);
    }

    return [wholePart, fractionPart].join('.');
  }

  return result;
};

const getCurrencyOptions = (currencies = []) =>
  currencies.map((currency) => ({ label: currency, value: currency }));

class PaymentOrdersForm extends Component {
  static propTypes = {
    intl: intlShape.isRequired,
    purses: array.isRequired,
    rules: array.isRequired,
    onSubmit: func.isRequired,
    loading: bool,
  };

  constructor(props) {
    super(props);
    const rules = getDefaultRules();
    const currency = props.currencies && props.currencies[0] ? props.currencies[0] : null;

    this.state = {
      data: {
        purse: null,
        amount: '',
        amountToPayout: '',
      },
      rules,
      errors: {},
      selectedCurrency: currency,
      smsConfirmModalShow: false,
      isCurrencyConverting: false,

      //TODO: придумать более понятное название,
      // напримео walletCurrency
      toCurrency: currency,
      currencyPopoverOpen: false,
      // paymentOptions
      // Не работает обновление страницы.
      // Возможны фатальные ошибки.
      paymentOptions: null,
    };
  }

  UNSAFE_componentWillReceiveProps(nextProps) {
    const { balance, currencies, history, webmasterWallets, onCurrencyChange, purses } = nextProps;
    if (this.state.data.amount === '' && balance && currencies && purses) {
      const balanceValues = balance.map(({ balance }) => balance);
      if (this.isVip()) {
        balance.forEach((balanceEl, index) => {
          const balanceHold = balanceEl.hold;
          const hold = balanceHold >= 0 ? balanceHold : 0;
          balanceValues[index] = balanceValues[index] + hold;
        });
      }

      let maxIndex = 0;

      let amount = '';
      let selectedCurrency = null;
      let selected = null;
      let purse = null;

      const { purseType } = parse(history.location.search);

      for (let i = 0; i < balanceValues.length; i++) {
        if (balanceValues[i] !== 0) {
          maxIndex = i;
          break;
        }
      }
      if (purses) {
        const wallets = purses.find(({ type }) => type === +purseType);
        if (wallets && wallets.wallets) {
          const currentWallet = wallets.wallets[0];
          purse = currentWallet.id;
          selectedCurrency = currentWallet.currency;
          selected = currentWallet.currency;
          const currencyIndex = currencies.findIndex((curr) => curr === selected);
          if (currencyIndex >= 0) {
            amount = balanceValues[currencyIndex];
          } else {
            amount = 0;
          }
        }
        this.setState((prevstate) => ({
          ...prevstate,
          paymentOptions: purses.length >= 1 ? this.getOptions(purses) : prevstate.paymentOptions,
        }));
      }

      if (purse === null) {
        if (currencies && currencies[maxIndex]) {
          amount = balanceValues[maxIndex];
          selectedCurrency = currencies[maxIndex];
          selected = currencies[maxIndex];
        } else {
          amount = balanceValues[0];
          selectedCurrency = currencies[0];
          selected = currencies[0];
        }
      }
      // set paymentOptions OFFERRUM-2374
      // purses && this.setState( (prev) => ({
      //   ...prev,
      //   paymentOptions: this.getOptions(purses)
      // }));

      onCurrencyChange(selectedCurrency);
      this.setState(({ data }) => ({
        data: {
          ...data,
          amount,
          amountToPayout: getTotal(
            amount,
            this.state.rules,
            this.state.selectedCurrency,
            this.state.toCurrency,
            this.props.rates,
            this.state.data.purse,
          ),
          purse: purse !== null ? purse : data.purse,
        },
        selectedCurrency,
        selected,
      }));
    }
  }

  isVip = () => this.props.profile.vip;
  // OFFERRUM-2374
  getOptions = (purses = [], inputChangeValue = null) => {
    if (inputChangeValue) {
      purses = purses.filter((purse, idx) => purse.title.includes(inputChangeValue));
    }
    const { isMember } = this.props;
    let returner = [];
    const { selected } = this.state;
    const notCreatedPurses = [];

    if (selected) {
      const available = {
        USD: [15, 32, 36, 41, 43],
        RUB: [3, 4, 5, 15, 29, 30, 31, 34, 35, 37, 41, 43],
        EUR: [27, 33],
      };

      const availableTypes = available[selected];
      availableTypes.forEach((type) => {
        const created = purses.find((purse) => purse.type === type);
        if (!created) {
          if (notCreatedPurses.indexOf(type) < 0) {
            notCreatedPurses.push(type);
          }
        }
      });
    }

    //ссылки на создание недобавленных кошельков
    let extraOptions = notCreatedPurses.map((type) => {
      return {
        value: { type, pathname: '/profile/payment-data' },
        // label: this.preparePurseOptionLabel({ type }),
        data: this.preparePurseOptionLabel({ type }),
      };
    });
    if (isMember) {
      extraOptions = [];
    }

    /* Сслылки на создание кошельков с типом 3 и 30 должны идти друг за другом */
    const cards = extraOptions.filter((purse) => purse.value.type === 3 || purse.value.type === 30);
    const withoutCards = extraOptions.filter(
      (purse) => purse.value.type !== 3 && purse.value.type !== 30,
    );
    extraOptions = [...withoutCards, ...cards];

    //переставить кошелек WMZ на первое место в списке, если он добавлен
    const WMZ = purses && purses.length > 0 && purses.filter((purse) => purse.type === 15);
    if (WMZ.length >= 1) {
      const reorderedPurses = [...WMZ, ...purses.filter((purse) => purse.type !== 15)];
      const result = [
        ...reorderedPurses.map(({ title, id: value, type, bonus, name }) => ({
          label: name ? `${title} ${name}` : title,
          name: name ? name : '',
          // label: this.preparePurseOptionLabel({ type, title, created: true, bonus, value }),
          data: this.preparePurseOptionLabel({ type, title, created: true, bonus, value, name }),
          value,
        })),
        ...extraOptions,
      ];

      return result.filter((item) => !!item.label);
    }
    const result = [
      ...purses.map(({ title, id: value, type, bonus, name }) => ({
        label: name ? `${title} ${name}` : title,
        name: name ? name : '',
        // label: this.preparePurseOptionLabel({ type, title, created: true, bonus, value }),
        data: this.preparePurseOptionLabel({ type, title, created: true, bonus, value, name }),
        value,
      })),
      ...extraOptions,
    ];
    return result.filter((item) => !!item.label);
  };
  // OFFERRUM-2374 создал функцию
  onInputChange = (inputValue) => {
    let { purses } = this.props;
    this.setState((prevState) => ({
      ...prevState,
      paymentOptions:
        purses.length >= 1 ? this.getOptions(purses, inputValue) : prevState.paymentOptions,
    }));
  };
  //title, commission, isNew, crown
  renderpurseOptionLabel = (options, index) => {
    let commissionText = options?.data?.commission ? options.data.commission : null;
    commissionText = commissionText === '0' ? '0%' : commissionText;
    const titleStyles = cn('purse-option-title', { isNew: options.data?.isNew });
    const commissionStyles = cn('purse-option-commission', {
      negative: commissionText && commissionText[0] === '-',
    });
    return (
      <div className="purse-option">
        <div className={titleStyles}>
          {options?.data && options.data?.name
            ? `${options.data?.title} ${options.data?.name}`
            : options?.data && options.data?.title
            ? `${options.data?.title}`
            : ''}
        </div>
        <div className={commissionStyles}>
          {options.data?.crown}
          {commissionText}
        </div>
      </div>
    );
  };

  preparePurseOptionLabel = ({ type, title, created, bonus, value, name }) => {
    const { wallets } = this.props;
    // const { webmasterWallets } = this.props;
    if (wallets) {
      const wallet = wallets.find((webWallets) => webWallets.type === type);
      if (!wallet) {
        return null;
      } else {
        const commission = bonus ? bonus : wallet.commissionText || wallet.commission;
        let crown = null;
        if (bonus) {
          const isMinus = bonus.includes('-');
          crown = (
            <Icon id={`commission-crown-${value}`} className="mr-2" type="crown">
              <UncontrolledTooltip placement="left" target={`commission-crown-${value}`}>
                <FormattedMessage
                  id={isMinus ? 'payments.crown.commission' : 'payments.crown.bonus'}
                  defaultMessage="Bonus"
                />
              </UncontrolledTooltip>
            </Icon>
          );
        }

        if (created) {
          // return this.renderpurseOptionLabel(title, commission, false, crown);
          return this.renderPurseOptionLabelSecondVersion(title, commission, false, crown, name);
        } else {
          // return this.renderpurseOptionLabel(wallet.title, commission, true, crown);
          return this.renderPurseOptionLabelSecondVersion(
            wallet.title,
            commission,
            true,
            crown,
            name,
          );
        }
      }
    }
  };
  renderPurseOptionLabelSecondVersion = (title, commission, inNew, crown, name) => ({
    title,
    commission,
    inNew,
    crown,
    name,
  });
  getFormattedMessage = (id, defaultMessage) =>
    this.props.intl.formatMessage({ id, defaultMessage });

  getRules = (purseId) => {
    const type = this.getType(purseId);

    if (type) {
      return this.props.rules.find((purse) => purse.type === type);
    }

    return getDefaultRules();
  };

  getType = (id) => {
    const purse = this.props.purses.find((purse) => purse.id === id);
    return purse ? purse.type : null;
  };

  /**
   * @param {number} id wallet id
   * @return {string} purse currency
   */
  getPurseCurrency = (id) => {
    const purse = this.props.purses.find((purse) => purse.id === id);
    return purse ? purse.currency : null;
  };

  /**
   * @param {string} currency
   */
  setCurrencyConvertingState = (currency) => {
    const { selectedCurrency } = this.state;
    const isCurrencyConverting = currency !== selectedCurrency;
    this.setState(() => ({
      isCurrencyConverting,
      toCurrency: currency,
      currencyPopoverOpen: isCurrencyConverting,
    }));
  };

  onAmountChange = (value, rules = {}, paymentCurrency, toCurrency, rates, purseId) => {
    const amount = value || '';
    if (!amount || amount.match(/^\d{1,}(\.\d{0,2})?$/)) {
      this.setState((prevState) => ({
        data: {
          ...prevState.data,
          amount,
          amountToPayout: getTotal(amount, rules, paymentCurrency, toCurrency, rates, purseId),
        },
      }));
    }
  };

  onToPayoutChange = (value, rules = {}, paymentCurrency, toCurrency, rates, purseId) => {
    const amount = value || '';
    if (!amount || amount.match(/^\d{1,}(\.\d{0,2})?$/)) {
      this.setState((prevState) => ({
        data: {
          ...prevState.data,
          amount: getInitialFromTotal(value, rules, paymentCurrency, toCurrency, rates, purseId),
          amountToPayout: amount,
        },
      }));
    }
  };

  handleCreationRedirect = ({ type, pathname }) => {
    const { setRedirectToPaymentsData } = this.props;
    if (pathname === '/profile/payment-data') {
      setRedirectToPaymentsData({ type, pathname });
    }
  };

  onSelectPurse = (selected) => {
    if (selected) {
      const { value: purse } = selected;
      const { setShowAlertPayment, showAlertPayment, setMessageAlert } = this.props;

      const currency = this.getPurseCurrency(purse);

      this.setCurrencyConvertingState(currency);

      this.handleCreationRedirect(purse);

      const rules = this.getRules(purse);

      if (rules.showAlerts && rules.type !== 15) {
        setShowAlertPayment(rules.showAlerts);
        setMessageAlert(rules.wallets[0].messageAlert);
      } else {
        setShowAlertPayment(false);
      }

      this.setState((prevState) => ({
        data: {
          ...prevState.data,
          purse,
          amountToPayout: getTotal(
            prevState.data.amount,
            rules,
            prevState.selectedCurrency,
            prevState.toCurrency,
            this.props.rates,
            purse,
          ),
        },
        rules,
      }));
    }
  };

  onSubmit = (e, verification_code) => {
    e.preventDefault();

    const { data, rules, isCurrencyConverting, selectedCurrency, toCurrency } = this.state;
    const { rates } = this.props;
    const errors = this.validate(
      data,
      rules,
      isCurrencyConverting,
      selectedCurrency,
      toCurrency,
      rates,
    );

    this.setState({ errors });

    if (Object.keys(errors).length === 0) {
      const { data, rules, isCurrencyConverting, selectedCurrency } = this.state;
      const { error, status, sessionId, rates, authType } = this.props;

      const payload = {
        payment_id: data.purse,
        payment_type: rules.type,
        payment_summ: data.amount,
      };

      if (authType) {
        switch (authType) {
          case 1:
            payload['sms_session_id'] = sessionId;
            payload['sms_code'] = verification_code;
            break;
          case 2: // google auth
            payload['token'] = verification_code;
            break;
          case 4: // telegram bot
            payload['telegram_code'] = verification_code;
            payload['telegram_session_id'] = sessionId;
            break;
        }
      } else if (sessionId) {
        payload['verification_session_id'] = sessionId;
        payload['verification_code'] = verification_code;
      }

      if (isCurrencyConverting) {
        Object.keys(rates).forEach((rate) => {
          payload[`rates[${rate.toLowerCase()}]`] = rates[rate];
        });
        payload['currency'] = selectedCurrency;
      }

      this.props.onSubmit(payload).then((response) => {
        if (response) {
          const { status, error } = response;
          if (status === 401) {
            this.setState(() => ({
              smsConfirmModalShow: true,
            }));
          } else if (status === 403) {
            this.setState(() => ({
              smsConfirmModalShow: false,
            }));
          }
        } else {
          this.setState(() => ({
            data: { purse: null, amount: '' },
            rules: getDefaultRules(),
            smsConfirmModalShow: false,
          }));
        }
      });
    }
  };

  toggleModal = () => {
    this.props.resetFormState();
    this.setState((prevState) => ({
      smsConfirmModalShow: !prevState.smsConfirmModalShow,
    }));
  };

  validate = (data, rules = {}, isCurrencyConverting, selectedCurrency, toCurrency, rates) => {
    console.log(rules);
    const errors = {};

    if (rules.maxPaymentDay !== null && data.amount > rules.maxPaymentDay) {
      errors.amount = (
        <FormattedMessage
          id="payments.limits.max-day"
          defaultMessage="Maximum payment sum per day is {limit}"
          values={{ limit: formatToCurrency(rules.maxPaymentDay) }}
        />
      );
    }

    const currencyTypeExceptions = [41, 43, 15];
    // Ограничение по amountToPayout для определенных типов валют
    if (data.amountToPayout > rules.maxPayment && currencyTypeExceptions.includes(rules.type)) {
      errors.amount = (
        <FormattedMessage
          id="payments.limits.max-per-one-to-be-paid"
          defaultMessage="Maximum payment sum per one is {limit}"
          values={{ limit: formatToCurrency(rules.maxPayment) }}
        />
      );
    }

    if (
      rules.maxPayment !== null &&
      data.amount > rules.maxPayment &&
      !currencyTypeExceptions.includes(rules.type)
    ) {
      errors.amount = (
        <FormattedMessage
          id="payments.limits.max-per-one-15"
          defaultMessage="Maximum payment sum per one is {limit}"
          values={{ limit: formatToCurrency(rules.maxPayment) }}
        />
      );
    }

    if (rules.maxPaymentMonth !== null && data.amount > rules.maxPaymentMonth) {
      errors.amount = (
        <FormattedMessage
          id="payments.limits.max-month"
          defaultMessage="Maximum payment sum per month is {limit}"
          values={{ limit: formatToCurrency(rules.maxPaymentMonth) }}
        />
      );
    }

    if (isCurrencyConverting) {
      if (
        Math.ceil(data.amountToPayout * rates[toCurrency]) <
        rules.minPayment * rates[toCurrency]
      ) {
        errors.amount = (
          <FormattedMessage
            id="payments.limits.min"
            defaultMessage="Minimum payment sum is {limit}"
            values={{
              limit: formatToCurrency(
                Math.ceil(
                  (rules.minPayment * rates[toCurrency] +
                    rules.fixedCommission * rates[toCurrency]) /
                    ((100 + parseFloat(rules.commission)) / 100),
                ),
              ),
            }}
          />
        );
      }
    } else if (data.amountTo < rules.minPayment) {
      errors.amount = (
        <FormattedMessage
          id="payments.limits.min"
          defaultMessage="Minimum payment sum is {limit}"
          values={{ limit: formatToCurrency(rules.minPayment) }}
        />
      );
    }

    // if(rules.maxPayment !== null && data.amount > rules.maxPayment) {
    //   errors.amount = (
    //     <FormattedMessage
    //       id="payments.limits.max"
    //       defaultMessage="Maximum sum per transaction is {limit}"
    //       values={{ limit: formatToCurrency(rules.maxPayment) }}
    //     />
    //   )
    // }

    return errors;
  };

  renderSelectNoResultsText = () => {
    const { intl } = this.props;
    return intl.formatMessage({
      id: 'payments.choose_purse.no-results-text',
      defaultMessage: 'No purses found',
    });
  };

  renderSelectCurrenciesNoResultsText = () => {
    const { intl } = this.props;
    return intl.formatMessage({
      id: 'payments.choose_currency.no-results-text',
      defaultMessage: 'No currencies found',
    });
  };

  onSelectCurrency = (selectedCurrency) => {
    const { onCurrencyChange } = this.props;
    const currency = selectedCurrency.value;
    const { selectedCurrency: currentCurrency } = this.state;

    if (currentCurrency && currentCurrency.value === currency) return;

    const { balance } = this.props;

    const { balance: balanceValue, hold } = balance.find((item) => item.currency === currency);
    let amount = balanceValue;
    if (amount < 0) {
      amount = 0;
    }
    if (this.isVip()) {
      const holdValue = hold > 0 ? hold : 0;
      amount = amount + holdValue;
    }

    onCurrencyChange(currency);

    this.setState((prevstate) => ({
      selectedCurrency: currency,
      selected: currency,
      data: {
        ...prevstate.data,
        amount,
        purse: null,
      },
      toCurrency: currency,
    }));
  };

  renderConverToCurrencyTooltip = () => {
    const { toCurrency, selectedCurrency, currencyPopoverOpen, rules } = this.state;
    const { rates } = this.props;
    return (
      <Popover
        trigger="legacy hover"
        placement="bottom"
        target="payments-total"
        isOpen={currencyPopoverOpen}
        toggle={this.toggleCurrencyPopover}
      >
        <PopoverBody className="popover-border">
          Максимальная сумма выплаты: {rules.maxPayment}&nbsp;
          {toCurrency} по курсу:{' '}
          <b>
            1&nbsp;{toCurrency} = {rates[`${selectedCurrency}${toCurrency}`]}&nbsp;
            {selectedCurrency}
          </b>
        </PopoverBody>
      </Popover>
    );
  };

  toggleCurrencyPopover = () => {
    this.setState((prevState) => ({
      currencyPopoverOpen: !prevState.currencyPopoverOpen,
    }));
  };

  render() {
    const {
      profile,
      isMember,
      showAlertPayment,
      setShowAlertPayment,
      messageAlert,
      setMessageAlert,
      showAlert,
    } = this.props;
    const { paymentOptions } = this.state;
    const isUserBlocked = profile && profile.banned;
    const isUserQuar = profile && profile.quar; // если юзер на карантине
    const isSlaveAndNotInMember = profile && profile.master.slave && !isMember;
    const data = { ...this.props, ...this.state, isUserBlocked, isSlaveAndNotInMember };

    return (
      <PaymentsOrdersFormView
        data={{ ...data }}
        onSubmit={this.onSubmit}
        onSelectCurrency={this.onSelectCurrency}
        getFormattedMessage={this.getFormattedMessage}
        onAmountChange={this.onAmountChange}
        getOptions={this.getOptions}
        showAlertPayment={showAlertPayment}
        messageAlert={messageAlert}
        showAlert={showAlert}
        setShowAlertPayment={setShowAlertPayment}
        setMessageAlert={setMessageAlert}
        // getOptions={paymentOptions}
        formatOptionLabel={this.renderpurseOptionLabel}
        onInputChange={this.onInputChange}
        onSelectPurse={this.onSelectPurse}
        toggleModal={this.toggleModal}
        renderConverToCurrencyTooltip={this.renderConverToCurrencyTooltip}
        getCurrencyOptions={getCurrencyOptions}
        getTotal={getTotal}
        onToPayoutChange={this.onToPayoutChange}
        isUserQuar={isUserQuar}
        selectCurrenciesNoResultsText={this.renderSelectCurrenciesNoResultsText()}
        selectNoResultsText={this.renderSelectNoResultsText()}
      />
    );
  }
}

const mapStateToProps = ({ webmaster, slaves, app }) => ({
  profile: webmaster.profile,
  webmasterWallets: webmaster.wallets,
  slaves: slaves,
  rates: app.globalSettings.currency_rates,
});

const mapDispatchToProps = (dispatch) => ({
  setRedirectToPaymentsData: (data) => dispatch(setRedirectToPaymentsData(data)),
});

export default connect(mapStateToProps, mapDispatchToProps)(injectIntl(PaymentOrdersForm));
